import { useEffect, useState } from "react";
import {
  collection,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { getPublicHandle, normalizePublicHandle } from "../utils/publicProfile";
import { ellipsizeProfileText } from "../utils/profileText";
import {
  getSponsoredBusinessAvatarMap,
  resolveSponsoredAvatar,
} from "../services/sponsoredAvatarService";
import { isSelfOnlyPrivateCapsule } from "../utils/messageClassification";
import { useReceivedCapsuleDocs } from "../contexts/UserBadgesContext";

const userDocCache = new Map();
function tsToMillis(ts) {
  return (
    ts?.toMillis?.() ||
    (typeof ts?.seconds === "number" ? ts.seconds * 1000 : 0)
  );
}

// Resolve sender profiles for a set of ids, batching the uncached ones into
// chunked `documentId() in` queries (10 per query) instead of one getDoc per
// sender. Resolved and known-missing users are cached; ids in a chunk whose
// query failed are left uncached so a later call can retry.
async function loadUserMap(senderIds) {
  const uniqueIds = Array.from(new Set(senderIds.filter(Boolean)));
  const missing = uniqueIds.filter((id) => !userDocCache.has(id));

  if (missing.length) {
    const chunks = [];
    for (let index = 0; index < missing.length; index += 10) {
      chunks.push(missing.slice(index, index + 10));
    }

    await Promise.all(
      chunks.map(async (chunk) => {
        try {
          const snap = await getDocs(
            query(collection(db, "users"), where(documentId(), "in", chunk))
          );
          const found = new Map();
          snap.docs.forEach((docSnap) => {
            found.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
          });
          chunk.forEach((id) => userDocCache.set(id, found.get(id) || { uid: id }));
        } catch (err) {
          console.error("Failed to batch-load sender profiles:", chunk, err);
        }
      })
    );
  }

  return uniqueIds.reduce((map, id) => {
    const user = userDocCache.get(id);
    if (user?.uid) map[id] = user;
    return map;
  }, {});
}

function mapReceivedMessage(snapshotDoc) {
  const data = snapshotDoc.data();
  return {
    id: snapshotDoc.id,
    senderId: data.senderId,
    subject: data.messageTitle || "",
    message: data.messageBody || "",
    revealDate: data.revealDate,
    sentAt: data.sentAt,
    archivedBy: data.archivedBy || [],
    deletedBy: data.deletedBy || [],
    readBy: data.readBy || [],
    unreadCommentUserIds: data.unreadCommentUserIds || [],
    address: data?.location?.address,
    location: data?.location || null,
    audience: data.audience || "",
    isSelfCapsule: data.isSelfCapsule === true,
    recipientUserIds: data.recipientUserIds || [],
    claimedBy: data.claimedBy || [],
    deliveryMode: data.deliveryMode || "",
    recipientPhoneNumbers: data.recipientPhoneNumbers || [],
    capsuleType: data.capsuleType || "",
    businessId: data.businessId || "",
    businessAvatarUrl: data.businessAvatarUrl || "",
    senderProfileImage: data.senderProfileImage || "",
    status: data.status || "",
    isDraft: data.isDraft === true,
    lifecycleStatus: data.lifecycleStatus || "",
    createdAt: data.createdAt || null,
  };
}

function isDraftMessage(message) {
  return (
    message?.isDraft === true ||
    String(message?.status || "").toLowerCase() === "draft" ||
    String(message?.lifecycleStatus || "").toLowerCase() === "draft"
  );
}

function mapSentMessage(snapshotDoc) {
  const data = snapshotDoc.data();
  return {
    id: snapshotDoc.id,
    senderId: data.senderId,
    subject: data.messageTitle || "",
    message: data.messageBody || "",
    revealDate: data.revealDate,
    sentAt: data.sentAt,
    archivedBy: data.archivedBy || [],
    deletedBy: data.deletedBy || [],
    readBy: data.readBy || [],
    unreadCommentUserIds: data.unreadCommentUserIds || [],
    address: data?.location?.address,
    location: data?.location || null,
    audience: data.audience || "",
    isSelfCapsule: data.isSelfCapsule === true,
    recipientUserIds: data.recipientUserIds || [],
    claimedBy: data.claimedBy || [],
    deliveryMode: data.deliveryMode || "",
    recipientPhoneNumbers: data.recipientPhoneNumbers || [],
    media: data.media || [],
    capsuleType: data.capsuleType || "",
    callToActionText: data.callToActionText || "",
    callToActionUrl: data.callToActionUrl || "",
    businessId: data.businessId || "",
    businessAvatarUrl: data.businessAvatarUrl || "",
    senderProfileImage: data.senderProfileImage || "",
    hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
    endDate: data.endDate || null,
    status: data.status || "",
    isDraft: data.isDraft === true,
    lifecycleStatus: data.lifecycleStatus || "",
    draftStep: data.draftStep || "",
    lastEditedAt: data.lastEditedAt || null,
    createdAt: data.createdAt || null,
  };
}

function mapArchivedMessage(snapshotDoc) {
  const data = snapshotDoc.data();
  return {
    id: snapshotDoc.id,
    senderId: data.senderId,
    subject: data.messageTitle || "",
    message: data.messageBody || "",
    revealDate: data.revealDate,
    sentAt: data.sentAt,
    archivedBy: data.archivedBy || [],
    deletedBy: data.deletedBy || [],
    readBy: data.readBy || [],
    address: data?.location?.address,
    location: data?.location || null,
    capsuleType: data.capsuleType || "",
    businessId: data.businessId || "",
    businessAvatarUrl: data.businessAvatarUrl || "",
    senderProfileImage: data.senderProfileImage || "",
    createdAt: data.createdAt || null,
  };
}

function getSenderPublicHandle(user = {}) {
  return (
    normalizePublicHandle(user?.publicHandle || "") ||
    getPublicHandle(user?.userName || "") ||
    ""
  );
}

function getSenderDisplayName(user = {}) {
  const displayName = String(user?.displayName || "").trim();
  if (displayName) return ellipsizeProfileText(displayName);

  const publicHandle = getSenderPublicHandle(user);
  if (publicHandle) return ellipsizeProfileText(publicHandle);

  return "Unknown";
}

export function useReceivedMessages(uid) {
  // Reuse the session-level received + claimed subscription (owned by
  // UserBadgesProvider) instead of opening a second identical pair here.
  const { docs: receivedCapsuleDocs, loaded: receivedLoaded } = useReceivedCapsuleDocs();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      setIsLoading(false);
      return undefined;
    }

    // Keep showing the loading state until the shared subscription has data.
    if (!receivedLoaded) {
      setIsLoading(true);
      return undefined;
    }

    let active = true;
    setIsLoading(true);

    (async () => {
      try {
        const raw = receivedCapsuleDocs.map(mapReceivedMessage);
        const [userMap, sponsoredBusinessAvatarMap] = await Promise.all([
          loadUserMap(raw.map((message) => message.senderId)),
          getSponsoredBusinessAvatarMap(
            raw
              .filter((message) => String(message?.capsuleType || "").toLowerCase() === "sponsored")
              .map((message) => message?.businessId || message?.senderId)
          ),
        ]);

        if (!active) return;

        const enriched = raw
          .filter(
            (message) =>
              !isDraftMessage(message) &&
              !isSelfOnlyPrivateCapsule(message, uid) &&
              !message.archivedBy.includes(uid) &&
              !message.deletedBy.includes(uid)
          )
          .map((message) => ({
            ...message,
            senderDisplayName: getSenderDisplayName(userMap[message.senderId]),
            senderAvatar: resolveSponsoredAvatar({
              capsuleType: message?.capsuleType,
              businessAvatarUrl:
                String(
                  message?.businessAvatarUrl ||
                  sponsoredBusinessAvatarMap[String(message?.businessId || message?.senderId || "").trim()] ||
                  ""
                ).trim(),
              creatorProfileImage: userMap[message.senderId]?.profileImage || "",
              senderProfileImage: message?.senderProfileImage || "",
            }),
            senderPublicHandle: getSenderPublicHandle(userMap[message.senderId]),
          }))
          .sort((a, b) => {
            const aTs = tsToMillis(a.sentAt) || tsToMillis(a.createdAt);
            const bTs = tsToMillis(b.sentAt) || tsToMillis(b.createdAt);
            return bTs - aTs;
          });

        setMessages(enriched);
      } catch (err) {
        console.error("Failed to refresh received messages:", err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [uid, receivedCapsuleDocs, receivedLoaded]);

  return {
    messages,
    isLoading,
    isLoadingMore: false,
    hasMore: false,
    loadMore: () => {},
  };
}

export function useSentMessages(uid) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);

    const sentQ = query(
      collection(db, "timeCapsules"),
      where("senderId", "==", uid),
      orderBy("createdAt", "desc")
    );

    const unsubS = onSnapshot(
      sentQ,
      (snap) => {
        const raw = snap.docs.map(mapSentMessage);
        setMessages(
          raw.filter(
            (message) =>
              !message.archivedBy.includes(uid) && !message.deletedBy.includes(uid)
          )
        );
        setIsLoading(false);
      },
      (err) => {
        console.error("Sent query failed:", err);
        setIsLoading(false);
      }
    );

    return () => {
      unsubS();
    };
  }, [uid]);

  return { messages, isLoading };
}

// Memory and Buried views both read the user's own private capsules with the
// identical query/fallback; only their client-side filter differs (memory
// excludes drafts, buried keeps them). Share a single subscription and derive
// both lists from it instead of opening two identical listeners.
export function useSelfPrivateMessages(uid) {
  const [memory, setMemory] = useState([]);
  const [buried, setBuried] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMemory([]);
      setBuried([]);
      setIsLoading(false);
      return undefined;
    }

    let active = true;
    let fallbackUnsub = null;
    setIsLoading(true);

    const sortByRecency = (items) =>
      items.slice().sort((a, b) => {
        const aTs = tsToMillis(a.sentAt) || tsToMillis(a.createdAt);
        const bTs = tsToMillis(b.sentAt) || tsToMillis(b.createdAt);
        return bTs - aTs;
      });

    const applyMessages = (docs) => {
      const raw = docs.map(mapSentMessage);
      const activePrivate = raw.filter(
        (message) =>
          String(message?.capsuleType || "").toLowerCase() === "private" &&
          !message.archivedBy.includes(uid) &&
          !message.deletedBy.includes(uid)
      );
      if (!active) return;
      // Buried keeps drafts; Memory excludes them.
      setBuried(sortByRecency(activePrivate));
      setMemory(sortByRecency(activePrivate.filter((message) => !isDraftMessage(message))));
      setIsLoading(false);
    };

    const startFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, "timeCapsules"),
        where("senderId", "==", uid),
        orderBy("createdAt", "desc")
      );

      fallbackUnsub = onSnapshot(
        fallbackQ,
        (snap) => {
          applyMessages(snap.docs);
        },
        (err) => {
          console.error("Self-private fallback query failed:", err);
          if (active) setIsLoading(false);
        }
      );
    };

    const selfPrivateQ = query(
      collection(db, "timeCapsules"),
      where("senderId", "==", uid),
      where("capsuleType", "==", "private"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      selfPrivateQ,
      (snap) => {
        applyMessages(snap.docs);
      },
      (err) => {
        console.warn("Self-private query failed, falling back to sent query:", err);
        startFallbackQuery();
      }
    );

    return () => {
      active = false;
      unsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }, [uid]);

  return { memory, buried, isLoading };
}

export function useArchivedMessages(uid) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      setIsLoading(false);
      return undefined;
    }

    let active = true;
    setIsLoading(true);
    const archivedQ = query(
      collection(db, "timeCapsules"),
      where("archivedBy", "array-contains", uid)
    );

    const unsubA = onSnapshot(
      archivedQ,
      async (snap) => {
        try {
          const raw = snap.docs.map(mapArchivedMessage);
          const [userMap, sponsoredBusinessAvatarMap] = await Promise.all([
            loadUserMap(raw.map((message) => message.senderId)),
            getSponsoredBusinessAvatarMap(
              raw
                .filter((message) => String(message?.capsuleType || "").toLowerCase() === "sponsored")
                .map((message) => message?.businessId || message?.senderId)
            ),
          ]);

          if (!active) return;

          const enriched = raw
            .filter((message) => !message.deletedBy.includes(uid))
            .map((message) => ({
              ...message,
              senderDisplayName: getSenderDisplayName(userMap[message.senderId]),
              senderAvatar: resolveSponsoredAvatar({
                capsuleType: message?.capsuleType,
                businessAvatarUrl:
                  String(
                    message?.businessAvatarUrl ||
                    sponsoredBusinessAvatarMap[String(message?.businessId || message?.senderId || "").trim()] ||
                    ""
                  ).trim(),
                creatorProfileImage: userMap[message.senderId]?.profileImage || "",
                senderProfileImage: message?.senderProfileImage || "",
              }),
              senderPublicHandle: getSenderPublicHandle(userMap[message.senderId]),
            }))
            .sort((a, b) => {
              const aTs = tsToMillis(a.sentAt) || tsToMillis(a.createdAt);
              const bTs = tsToMillis(b.sentAt) || tsToMillis(b.createdAt);
              return bTs - aTs;
            });

          setMessages(enriched);
        } catch (err) {
          console.error("Archived query processing failed:", err);
        } finally {
          if (active) setIsLoading(false);
        }
      },
      (err) => {
        console.error("Archived query failed:", err);
        if (active) setIsLoading(false);
      }
    );

    return () => {
      active = false;
      unsubA();
    };
  }, [uid]);

  return { messages, isLoading };
}

export function useDeletedMessages(uid) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      setIsLoading(false);
      return undefined;
    }

    let active = true;
    setIsLoading(true);
    const deletedQ = query(
      collection(db, "timeCapsules"),
      where("deletedBy", "array-contains", uid)
    );

    const unsubD = onSnapshot(
      deletedQ,
      async (snap) => {
        try {
          const raw = snap.docs.map(mapArchivedMessage);
          const [userMap, sponsoredBusinessAvatarMap] = await Promise.all([
            loadUserMap(raw.map((message) => message.senderId)),
            getSponsoredBusinessAvatarMap(
              raw
                .filter((message) => String(message?.capsuleType || "").toLowerCase() === "sponsored")
                .map((message) => message?.businessId || message?.senderId)
            ),
          ]);

          if (!active) return;

          const enriched = raw.map((message) => ({
            ...message,
            senderDisplayName: getSenderDisplayName(userMap[message.senderId]),
            senderAvatar: resolveSponsoredAvatar({
              capsuleType: message?.capsuleType,
              businessAvatarUrl:
                String(
                  message?.businessAvatarUrl ||
                  sponsoredBusinessAvatarMap[String(message?.businessId || message?.senderId || "").trim()] ||
                  ""
                ).trim(),
              creatorProfileImage: userMap[message.senderId]?.profileImage || "",
              senderProfileImage: message?.senderProfileImage || "",
            }),
            senderPublicHandle: getSenderPublicHandle(userMap[message.senderId]),
          }))
          .sort((a, b) => {
            const aTs = tsToMillis(a.sentAt) || tsToMillis(a.createdAt);
            const bTs = tsToMillis(b.sentAt) || tsToMillis(b.createdAt);
            return bTs - aTs;
          });

          setMessages(enriched);
        } catch (err) {
          console.error("Deleted query processing failed:", err);
        } finally {
          if (active) setIsLoading(false);
        }
      },
      (err) => {
        console.error("Deleted query failed:", err);
        if (active) setIsLoading(false);
      }
    );

    return () => {
      active = false;
      unsubD();
    };
  }, [uid]);

  return { messages, isLoading };
}
